import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, spring, interpolate } from 'remotion';

/**
 * Morphing Shape Grid - 形态变换网格
 * 
 * 技术要点：
 * - 6x6 网格（36 个形态）
 * - 4 种形态变换（圆形 → 正方形 → 三角形 → 菱形）
 * - 错开式动画（基于行列位置）
 * - HSL 颜色渐变（基于位置和时间）
 * - Spring 物理动画
 * 
 * 适用场景：
 * - 品牌动画、背景装饰、创意视频、UI 动画
 */
export const MorphingShapeGrid: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const gridSize = 6;
  const cellSize = 150;
  const gap = 20;
  const totalSize = gridSize * (cellSize + gap) - gap;
  const startX = (1920 - totalSize) / 2;
  const startY = (1080 - totalSize) / 2;

  // 形态变换周期（4 秒一个循环）
  const morphCycle = 120; // 4 秒
  const morphProgress = (frame % morphCycle) / morphCycle;

  // 形态类型：0=圆形, 1=正方形, 2=三角形, 3=菱形
  const currentShape = Math.floor(morphProgress * 4) % 4;
  const nextShape = (currentShape + 1) % 4;
  const shapeBlend = (morphProgress * 4) % 1;

  // 生成形状路径
  const getShapePath = (shape: number, blend: number, size: number): string => {
    const radius = size / 2;

    if (shape === 0) {
      // 圆形（用多边形近似）
      const points = 32;
      const pathParts = [];
      for (let i = 0; i <= points; i++) {
        const angle = (i / points) * Math.PI * 2;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        pathParts.push(i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`);
      }
      return pathParts.join(' ') + ' Z';
    } else if (shape === 1) {
      // 正方形
      const half = radius * 0.9;
      return `M ${-half} ${-half} L ${half} ${-half} L ${half} ${half} L ${-half} ${half} Z`;
    } else if (shape === 2) {
      // 三角形
      const h = radius * 1.2;
      return `M 0 ${-h} L ${h * 0.866} ${h * 0.5} L ${-h * 0.866} ${h * 0.5} Z`;
    } else {
      // 菱形
      const half = radius * 1.1;
      return `M 0 ${-half} L ${half} 0 L 0 ${half} L ${-half} 0 Z`;
    }
  };

  // 混合两种形状
  const getBlendedPath = (blend: number, size: number): string => {
    const path1 = getShapePath(currentShape, blend, size);
    const path2 = getShapePath(nextShape, blend, size);
    // 简化：使用插值实现（实际 SVG 形态变换需要更复杂的算法）
    return blend < 0.5 ? path1 : path2;
  };

  const cells = [];
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const index = row * gridSize + col;
      const x = startX + col * (cellSize + gap) + cellSize / 2;
      const y = startY + row * (cellSize + gap) + cellSize / 2;

      // 错开式动画延迟
      const delay = (row + col) * 0.05;
      const springAnim = spring({
        frame: frame - delay * fps,
        fps,
        config: {
          damping: 15,
          stiffness: 100,
          mass: 1,
        },
      });

      // 缩放和旋转动画
      const scale = interpolate(springAnim, [0, 1], [0, 1]);
      const rotation = interpolate(springAnim, [0, 1], [-180, 0]);
      const opacity = interpolate(springAnim, [0, 1], [0, 1]);

      // HSL 颜色（基于位置和时间）
      const hue = (index * 10 + frame * 0.5) % 360;
      const saturation = 70;
      const lightness = 50 + Math.sin(frame * 0.05 + index * 0.2) * 15;

      cells.push(
        <g
          key={index}
          transform={`translate(${x}, ${y}) scale(${scale}) rotate(${rotation})`}
          opacity={opacity}
        >
          <path
            d={getBlendedPath(shapeBlend, cellSize * 0.8)}
            fill={`hsl(${hue}, ${saturation}%, ${lightness}%)`}
            stroke={`hsl(${hue}, ${saturation + 20}%, ${lightness + 20}%)`}
            strokeWidth={3}
          />
        </g>
      );
    }
  }

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0a0a0a',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <svg width={1920} height={1080}>
        {/* 背景网格装饰 */}
        <defs>
          <pattern
            id="grid"
            width={60}
            height={60}
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 60 0 L 0 0 0 60"
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth={1}
            />
          </pattern>
        </defs>
        <rect width={1920} height={1080} fill="url(#grid)" />

        {/* 形态网格 */}
        {cells}

        {/* 标题 */}
        <text
          x={960}
          y={980}
          textAnchor="middle"
          fill="white"
          fontSize={48}
          fontFamily="Arial, sans-serif"
          fontWeight="bold"
          opacity={interpolate(
            spring({ frame: frame - 30, fps, config: { damping: 20 } }),
            [0, 1],
            [0, 1]
          )}
        >
          #411 Morphing Shape Grid
        </text>
      </svg>
    </AbsoluteFill>
  );
};
